import { FinancingConfigDto } from '@applyonline/shared'
import { FORM_ID, InputText } from '@applyonline/shared/components'
import { useFormAutoSave } from '@applyonline/shared/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { formatCurrencyNoDecimals } from '@if/core/utils'
import { SubmitHandler, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { LoanAmountInput, buildLoanAmountInputSchema } from '../../../types/Schemas/PrequalificationSchema'

interface Props {
  prequalificationData: LoanAmountInput
  onPrequalificationChanged: (data: LoanAmountInput) => void
  nextStepAfter: (currentStep: string) => void
  currentStep: string
  financingProgramConfig: FinancingConfigDto
}

const LoanAmountForm = ({
  prequalificationData,
  onPrequalificationChanged,
  nextStepAfter,
  currentStep,
  financingProgramConfig,
}: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation()
  const {
    handleSubmit,
    register,
    formState: { errors },
    subscribe,
  } = useForm<LoanAmountInput>({
    mode: 'onBlur',
    resolver: yupResolver(buildLoanAmountInputSchema(financingProgramConfig)),
    defaultValues: prequalificationData,
  })

  useFormAutoSave(subscribe, onPrequalificationChanged)

  const onSubmit: SubmitHandler<LoanAmountInput> = (data) => {
    onPrequalificationChanged(data)
    nextStepAfter(currentStep)
  }

  return (
    <section className="step-content">
      <h3 className="form-question">{t('loanAmount.desiredAmount')}</h3>
      <div className="paragraph">
        <p>
          <Trans
            t={t}
            i18nKey="loanAmount.amountThresholds"
            values={{
              minLoanAmount: formatCurrencyNoDecimals(financingProgramConfig.minimumLoanAmount),
              maxLoanAmount: formatCurrencyNoDecimals(financingProgramConfig.maximumLoanAmount),
            }}
          />
        </p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
        <InputText
          type="number"
          inputMode="decimal"
          error={errors.requestedLoanAmount}
          label={t('loanAmount.amount')}
          wrapStyle={resolvedLanguage === 'en' ? 'number-wrap startwith' : 'number-wrap endwith'}
          {...register('requestedLoanAmount')}
        />
      </form>
    </section>
  )
}

export default LoanAmountForm
